import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { caseRing } from "@/mocks/home";

type Origin = { rect: DOMRect; src: string } | null;

type Props = {
  caseId: string | null;
  origin: Origin;
  onClose: () => void;
  getCurrentOriginRect?: (id: string) => DOMRect | null;
};

export default function CaseDetailOverlay({ caseId, origin, onClose, getCurrentOriginRect }: Props) {
  const c = caseRing.find((x) => x.id === caseId) || null;

  const [mounted, setMounted] = useState<boolean>(!!c);
  const [contentVisible, setContentVisible] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const flyRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);
  const sourceTileRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (c) setMounted(true);
  }, [c]);

  // Lock body scroll while mounted. Use a layout effect so it runs BEFORE the
  // fly animation, and compensate the scrollbar width so the layout doesn't
  // shift when the scrollbar disappears.
  useLayoutEffect(() => {
    if (!mounted) return;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [mounted]);

  // Open animation: fly from origin rect → target rect
  useLayoutEffect(() => {
    if (!mounted || !c || !origin) return;
    const fly = flyRef.current;
    const target = targetRef.current;
    const overlay = overlayRef.current;
    if (!fly || !target || !overlay) return;

    // If a previous close animation is still running (user clicked another
    // card very fast), cancel it and wipe all residual inline styles so this
    // new open starts from a perfectly clean slate.
    gsap.killTweensOf([fly, overlay]);
    closingRef.current = false;
    gsap.set(fly, { clearProps: "all" });
    // Restore any tile that a previous open had hidden but never got to un-hide.
    if (sourceTileRef.current) {
      sourceTileRef.current.style.visibility = "";
      sourceTileRef.current.style.transform =
        "perspective(700px) rotateX(0) rotateY(0) translateZ(0) scale(1)";
      sourceTileRef.current = null;
    }

    // Hide the origin tile so we don't see a duplicate underneath
    const idx = caseRing.findIndex((x) => x.id === c.id);
    const tile = idx >= 0 ? document.querySelector<HTMLElement>(`[data-ring-index="${idx}"]`) : null;
    sourceTileRef.current = tile;
    if (tile) tile.style.visibility = "hidden";

    const tRect = target.getBoundingClientRect();
    const o = origin.rect;

    gsap.set(overlay, { autoAlpha: 0 });
    gsap.set(fly, {
      position: "fixed",
      left: o.left,
      top: o.top,
      width: o.width,
      height: o.height,
      borderRadius: 10,
      overflow: "hidden",
      zIndex: 80,
      willChange: "transform, left, top, width, height, border-radius",
    });

    setContentVisible(false);

    const tl = gsap.timeline({
      onComplete: () => {
        // Show the detail image synchronously in the same frame we hide the
        // flying image, so there is no blank frame / flash in between.
        if (target) target.style.opacity = "1";
        setContentVisible(true);
        gsap.set(fly, { autoAlpha: 0 });
      },
    });
    gsap.set(fly, { autoAlpha: 1 });
    tl.to(overlay, { autoAlpha: 1, duration: 0.45, ease: "power2.out" }, 0);
    tl.to(
      fly,
      {
        left: tRect.left,
        top: tRect.top,
        width: tRect.width,
        height: tRect.height,
        borderRadius: 16,
        duration: 0.9,
        ease: "expo.inOut",
      },
      0,
    );

    return () => {
      if (sourceTileRef.current) {
        sourceTileRef.current.style.visibility = "";
        sourceTileRef.current.style.transform =
          "perspective(700px) rotateX(0) rotateY(0) translateZ(0) scale(1)";
        sourceTileRef.current = null;
      }
    };
  }, [mounted, c, origin]);

  const handleClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    const fly = flyRef.current;
    const target = targetRef.current;
    const overlay = overlayRef.current;
    if (!fly || !overlay || !c) {
      if (sourceTileRef.current) {
        sourceTileRef.current.style.visibility = "";
        sourceTileRef.current.style.transform =
          "perspective(700px) rotateX(0) rotateY(0) translateZ(0) scale(1)";
      }
      onClose();
      setMounted(false);
      closingRef.current = false;
      return;
    }

    // Hide the underlying detail image so only the fly image is visible during shrink
    setContentVisible(false);
    // Re-show the fly at the target position before shrinking back
    const tRect = target ? target.getBoundingClientRect() : null;
    if (tRect) {
      gsap.set(fly, {
        left: tRect.left,
        top: tRect.top,
        width: tRect.width,
        height: tRect.height,
        borderRadius: 16,
        autoAlpha: 1,
      });
    } else {
      gsap.set(fly, { autoAlpha: 1 });
    }

    // Compute current origin rect (in case user scrolled underneath — unlikely but safe)
    const backRect =
      (getCurrentOriginRect && getCurrentOriginRect(c.id)) ||
      origin?.rect ||
      (target ? target.getBoundingClientRect() : null);

    const tl = gsap.timeline({
      onComplete: () => {
        if (sourceTileRef.current) {
          sourceTileRef.current.style.visibility = "";
          sourceTileRef.current.style.transform =
            "perspective(700px) rotateX(0) rotateY(0) translateZ(0) scale(1)";
        }
        sourceTileRef.current = null;
        // Clear ALL residual inline styles from the fly div so the next open
        // starts from a clean slate and doesn't show a ghost layer underneath.
        gsap.set(fly, { clearProps: "all" });
        onClose();
        setMounted(false);
        closingRef.current = false;
      },
    });

    if (backRect) {
      tl.to(
        fly,
        {
          left: backRect.left,
          top: backRect.top,
          width: backRect.width,
          height: backRect.height,
          borderRadius: 10,
          duration: 0.85,
          ease: "expo.inOut",
        },
        0,
      );
    }
    tl.to(overlay, { autoAlpha: 0, duration: 0.5, ease: "power2.inOut" }, 0.15);
  };

  // ESC to close
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
     
  }, [mounted]);

  // If caseId became null externally without close anim, still unmount cleanly
  useEffect(() => {
    if (!caseId && mounted && !closingRef.current) {
      setMounted(false);
    }
  }, [caseId, mounted]);

  if (!mounted || !c) return null;

  const flySrc = origin?.src || c.image;

  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 z-[70] bg-white overflow-y-auto"
        style={{ opacity: 0, visibility: "hidden" }}
      >
        <div className="w-full px-6 md:px-10 py-6 flex items-center justify-between border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3 text-[12px] text-ink-muted">
            <span className="text-brand">Real Results</span>
            <span>/</span>
            <span className="text-ink">{c.name}</span>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="w-full px-6 md:px-10 py-12 md:py-16">
          <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div
                ref={targetRef}
                className="w-full aspect-[4/5] rounded-editorial overflow-hidden bg-page-cool"
                style={{ opacity: contentVisible ? 1 : 0 }}
              >
                <img src={c.image} alt={c.name} className="w-full h-full object-cover object-top" />
              </div>
            </div>
            <div
              className="lg:col-span-5"
              style={{
                opacity: contentVisible ? 1 : 0,
                transform: contentVisible ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 500ms ease, transform 600ms ease",
              }}
            >
              <div className="text-[11px] tracking-[0.25em] text-brand uppercase mb-3">Case Study</div>
              <h1 className="text-[36px] md:text-[48px] tracking-[-0.02em] font-medium text-ink">{c.name}</h1>
              <div className="text-[14px] text-ink-muted mt-2">{c.treatment}</div>

              <div className="mt-10 space-y-6 text-[14px] text-ink-muted leading-relaxed">
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-ink mb-2">Initial Concern</div>
                  <p>Existing restorations no longer matched the surrounding teeth, with visible edge lines and a slight color mismatch after several years.</p>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-ink mb-2">Treatment Plan</div>
                  <p>Digital smile design followed by a set of custom porcelain restorations, matched to the patient's natural tone and translucency.</p>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-ink mb-2">Timeline</div>
                  <p>3 clinic visits over 4 weeks.</p>
                </div>
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-ink mb-2">Doctor Note</div>
                  <p className="text-ink">"We kept the character of the smile intact — it should still look like the same person, just fresher." — Dr. Ava Nordin</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Flying shared image — starts hidden; gsap.set in the layout effect
          sets position/size/visibility before the animation begins, so this
          element never flashes at position 0,0. */}
      <div ref={flyRef} className="pointer-events-none" style={{ visibility: "hidden" }}>
        <img
          src={flySrc}
          alt={c.name}
          className="w-full h-full object-cover object-top block"
        />
      </div>
    </>
  );
}